import axios from '../../utils/axiosConfig';
import { toast } from 'react-hot-toast';

// Action Types
export const ATTACK_HEALTH_REQUEST = 'ATTACK_HEALTH_REQUEST';
export const ATTACK_HEALTH_SUCCESS = 'ATTACK_HEALTH_SUCCESS';
export const ATTACK_HEALTH_FAIL = 'ATTACK_HEALTH_FAIL';

export const ATTACK_RECENT_ALERTS_REQUEST = 'ATTACK_RECENT_ALERTS_REQUEST';
export const ATTACK_RECENT_ALERTS_SUCCESS = 'ATTACK_RECENT_ALERTS_SUCCESS';
export const ATTACK_RECENT_ALERTS_FAIL = 'ATTACK_RECENT_ALERTS_FAIL';

export const ATTACK_ENRICH_REQUEST = 'ATTACK_ENRICH_REQUEST';
export const ATTACK_ENRICH_SUCCESS = 'ATTACK_ENRICH_SUCCESS';
export const ATTACK_ENRICH_FAIL = 'ATTACK_ENRICH_FAIL';

export const ATTACK_MAP_TECHNIQUE_REQUEST = 'ATTACK_MAP_TECHNIQUE_REQUEST';
export const ATTACK_MAP_TECHNIQUE_SUCCESS = 'ATTACK_MAP_TECHNIQUE_SUCCESS';
export const ATTACK_MAP_TECHNIQUE_FAIL = 'ATTACK_MAP_TECHNIQUE_FAIL';

export const ATTACK_CO_OCCURRENCES_REQUEST = 'ATTACK_CO_OCCURRENCES_REQUEST';
export const ATTACK_CO_OCCURRENCES_SUCCESS = 'ATTACK_CO_OCCURRENCES_SUCCESS';
export const ATTACK_CO_OCCURRENCES_FAIL = 'ATTACK_CO_OCCURRENCES_FAIL';

export const ATTACK_TECHNIQUES_REQUEST = 'ATTACK_TECHNIQUES_REQUEST';
export const ATTACK_TECHNIQUES_SUCCESS = 'ATTACK_TECHNIQUES_SUCCESS';
export const ATTACK_TECHNIQUES_FAIL = 'ATTACK_TECHNIQUES_FAIL';

export const ATTACK_DECIDER_URL_REQUEST = 'ATTACK_DECIDER_URL_REQUEST';
export const ATTACK_DECIDER_URL_SUCCESS = 'ATTACK_DECIDER_URL_SUCCESS';
export const ATTACK_DECIDER_URL_FAIL = 'ATTACK_DECIDER_URL_FAIL';

/**
 * Check Decider health/connectivity
 */
export const checkAttackHealth = () => async (dispatch) => {
  try {
    dispatch({ type: ATTACK_HEALTH_REQUEST });

    const { data } = await axios.get('/api/attack/health');

    dispatch({
      type: ATTACK_HEALTH_SUCCESS,
      payload: data
    });

    return data;
  } catch (error) {
    const message = error.response?.data?.msg || error.message || 'Failed to check Decider connectivity';
    
    dispatch({
      type: ATTACK_HEALTH_FAIL,
      payload: message
    });

    return { status: false, connected: false, msg: message };
  }
};

/**
 * Get recent alerts with ATT&CK mappings
 */
export const getRecentAlerts = (filters = {}) => async (dispatch) => {
  try {
    dispatch({ type: ATTACK_RECENT_ALERTS_REQUEST });

    const params = new URLSearchParams();
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.watcher_id) params.append('watcher_id', filters.watcher_id);

    const { data } = await axios.get(`/api/attack/recent-alerts?${params.toString()}`);

    if (data.status) {
      dispatch({
        type: ATTACK_RECENT_ALERTS_SUCCESS,
        payload: data
      });
    } else {
      dispatch({
        type: ATTACK_RECENT_ALERTS_FAIL,
        payload: data.msg || 'Failed to fetch alerts'
      });
    }

    return data;
  } catch (error) {
    const message = error.response?.data?.msg || error.message || 'Failed to fetch alerts';
    
    dispatch({
      type: ATTACK_RECENT_ALERTS_FAIL,
      payload: message
    });

    toast.error(message);
    return { status: false, msg: message };
  }
};

/**
 * Enrich an alert with ATT&CK techniques
 */
export const enrichAlert = (alertId, alertText) => async (dispatch) => {
  try {
    dispatch({ type: ATTACK_ENRICH_REQUEST });

    const { data } = await axios.post('/api/attack/enrich', {
      alert_id: alertId,
      alert_text: alertText
    });

    if (data.status) {
      dispatch({
        type: ATTACK_ENRICH_SUCCESS,
        payload: data
      });
      
      if (data.suggestions && data.suggestions.length > 0) {
        toast.success(`Found ${data.suggestions.length} technique suggestion(s)`);
      } else {
        toast('No matching techniques found');
      }
    } else {
      dispatch({
        type: ATTACK_ENRICH_FAIL,
        payload: data.msg || 'Failed to enrich alert'
      });
      toast.error(data.msg || 'Failed to enrich alert');
    }

    return data;
  } catch (error) {
    const message = error.response?.data?.msg || error.message || 'Failed to enrich alert';
    
    dispatch({
      type: ATTACK_ENRICH_FAIL,
      payload: message
    });

    toast.error(message);
    return { status: false, msg: message };
  }
};

/**
 * Map a technique to an alert
 */
export const mapTechnique = (alertId, techniqueId, techniqueName, confidence = 'manual', notes = '') => async (dispatch) => {
  try {
    dispatch({ type: ATTACK_MAP_TECHNIQUE_REQUEST });

    const { data } = await axios.post('/api/attack/map-technique', {
      alert_id: alertId,
      technique_id: techniqueId,
      technique_name: techniqueName,
      confidence: confidence,
      notes: notes
    });

    if (data.status) {
      dispatch({
        type: ATTACK_MAP_TECHNIQUE_SUCCESS,
        payload: data
      });
      toast.success('Technique mapped successfully');
    } else {
      dispatch({
        type: ATTACK_MAP_TECHNIQUE_FAIL,
        payload: data.msg || 'Failed to map technique'
      });
      toast.error(data.msg || 'Failed to map technique');
    }

    return data;
  } catch (error) {
    const message = error.response?.data?.msg || error.message || 'Failed to map technique';
    
    dispatch({
      type: ATTACK_MAP_TECHNIQUE_FAIL,
      payload: message
    });

    toast.error(message);
    return { status: false, msg: message };
  }
};

/**
 * Get co-occurring techniques
 */
export const getCoOccurrences = (techniqueIds) => async (dispatch) => {
  try {
    dispatch({ type: ATTACK_CO_OCCURRENCES_REQUEST });

    const { data } = await axios.post('/api/attack/co-occurrences', {
      technique_ids: techniqueIds
    });

    if (data.status) {
      dispatch({
        type: ATTACK_CO_OCCURRENCES_SUCCESS,
        payload: data
      });
    } else {
      dispatch({
        type: ATTACK_CO_OCCURRENCES_FAIL,
        payload: data.msg || 'Failed to fetch co-occurrences'
      });
    }

    return data;
  } catch (error) {
    const message = error.response?.data?.msg || error.message || 'Failed to fetch co-occurrences';
    
    dispatch({
      type: ATTACK_CO_OCCURRENCES_FAIL,
      payload: message
    });

    return { status: false, msg: message };
  }
};

/**
 * Get predefined attack patterns/techniques
 */
export const getTechniques = () => async (dispatch) => {
  try {
    dispatch({ type: ATTACK_TECHNIQUES_REQUEST });

    const { data } = await axios.get('/api/attack/techniques');

    if (data.status) {
      dispatch({
        type: ATTACK_TECHNIQUES_SUCCESS,
        payload: data
      });
    } else {
      dispatch({
        type: ATTACK_TECHNIQUES_FAIL,
        payload: data.msg || 'Failed to fetch techniques'
      });
    }

    return data;
  } catch (error) {
    const message = error.response?.data?.msg || error.message || 'Failed to fetch techniques';
    
    dispatch({
      type: ATTACK_TECHNIQUES_FAIL,
      payload: message
    });

    return { status: false, msg: message };
  }
};

/**
 * Generate Decider URL for a technique or tactic
 */
export const getDeciderUrl = (techId = null, tacticId = null) => async (dispatch) => {
  try {
    dispatch({ type: ATTACK_DECIDER_URL_REQUEST });

    const { data } = await axios.post('/api/attack/decider-url', {
      tech_id: techId,
      tactic_id: tacticId
    });

    if (data.status) {
      dispatch({
        type: ATTACK_DECIDER_URL_SUCCESS,
        payload: data
      });
      
      // Open URL in new tab
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } else {
      dispatch({
        type: ATTACK_DECIDER_URL_FAIL,
        payload: data.msg || 'Failed to generate Decider URL'
      });
      toast.error(data.msg || 'Failed to generate Decider URL');
    }

    return data;
  } catch (error) {
    const message = error.response?.data?.msg || error.message || 'Failed to generate Decider URL';
    
    dispatch({
      type: ATTACK_DECIDER_URL_FAIL,
      payload: message
    });

    toast.error(message);
    return { status: false, msg: message };
  }
};

